import React from 'react';

import { Badge } from 'src/component/ds/ui/badge';
import { SEVERITY_ICON, SEVERITY_STYLE } from 'src/screen/v2/Protected/DashboardScreen/AlarmsToTriage/severity';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/component/ds/ui/tooltip';
import { cn } from 'src/component/ds/utils';
import { useTranslation } from 'src/i18n';
import type { UnitAlarmCount } from './alarmVolume';

/**
 * Sits beside the tile grid with the alarms on devices that have no unit, so
 * the tiles plus this add up to Alarms to Triage. Kept out of the grid and
 * explained by a tooltip so nobody reads "No area" as an area's name.
 *
 * @param {object} props the panel's props
 * @param {UnitAlarmCount[]} props.counts the non-zero severity slots, most severe first
 * @returns {JSX.Element} the panel
 */
export default function UnassignedSummary ({ counts }: { counts: UnitAlarmCount[] }) {
  const { t } = useTranslation();
  const total = counts.reduce((sum, { count }) => sum + count, 0);

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            tabIndex={0}
            className={cn(
              // Two tile rows (89px each) plus the 8px gap between them.
              'ds-flex ds-min-h-[89px] ds-flex-col ds-gap-1 ds-rounded-ds ds-border ds-border-border ds-p-2.5',
              'ds-narrow:ds-h-[186px]',
              'ds-cursor-default',
              'focus-visible:ds-outline-none focus-visible:ds-shadow-ds-focus'
            )}
            data-testid='alarm-volume-unassigned'
          >
            <span className='ds-text-ds-mini ds-uppercase ds-text-muted-foreground'>
              {t('dashboard.alarmVolumeUnassigned')}
            </span>
            <span
              className='ds-font-heading ds-text-ds-h2 ds-font-medium ds-text-foreground'
              data-testid='alarm-volume-unassigned-total'
            >
              {total}
            </span>
            <span className='ds-text-ds-mini ds-text-muted-foreground'>
              {t('dashboard.alarmVolumeUnassignedCaption')}
            </span>

            <span className='ds-mt-auto ds-flex ds-flex-wrap ds-items-center ds-gap-1 ds-pt-2'>
              {counts.map(({ severity, count, label }) => {
                const Icon = SEVERITY_ICON[severity];

                return (
                  <Badge
                    key={severity}
                    variant={null}
                    className={cn(SEVERITY_STYLE[severity].bar, 'ds-text-primary-foreground')}
                    aria-label={t('dashboard.alarmVolumeBadgeLabel', { count, label })}
                    data-testid={`alarm-volume-unassigned-badge-${severity}`}
                  >
                    <Icon aria-hidden='true' fontSize='inherit' />
                    {count}
                  </Badge>
                );
              })}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent data-testid='alarm-volume-unassigned-hint'>
          {t('dashboard.alarmVolumeUnassignedHint')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
